/**
 * LanguagePackEditor — iter-26 (P1) admin authoring UI.
 *
 * Self-contained editor for one concept's `language_pack`. The admin
 * picks a language tab (EN · AR · MI), edits the localised fields,
 * and saves to the per-slot endpoint:
 *
 *   GET    /api/admin/concept_configs/{cid}/language_pack
 *   PUT    /api/admin/concept_configs/{cid}/language_pack/{lang}
 *   POST   /api/admin/concept_configs/{cid}/language_pack/{lang}/mt
 *
 * The MT button calls the server-side Gemini helper which stamps every
 * translated string with `[MT — pending review] ` so the admin can
 * easily see which strings still need human-translator review.
 */
import { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { api } from "../../lib/api";

const LANG_LABEL = {
  en: "English",
  ar: "العربية (Arabic)",
  mi: "te reo Māori",
};

const FIELD_HINT = {
  candidate_task: "What the candidate is asked to DO. Shown above the simulation.",
  candidate_goal: "What the assessor is measuring. Shown in the audit panel only.",
  scenario_text: "JSON object — inbox messages, browser results, file contents, workflow steps, etc. Country tokens like {{BANK}} substitute at runtime.",
  remediation_message: "Shown on the candidate's outcome page if they fail.",
  reflection_prompt: "Open-ended question shown on the reflection step.",
  reflection_required_themes: "Comma-separated keyword tokens the AI evaluator looks for in the candidate's reflection.",
};

export default function LanguagePackEditor({ conceptId, onClose }) {
  const [data, setData] = useState(null);
  const [activeLang, setActiveLang] = useState("en");
  const [busy, setBusy] = useState(false);
  const [mtBusy, setMtBusy] = useState(false);

  // Load the language_pack + completeness once on mount.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api
          .get(`/admin/concept_configs/${conceptId}/language_pack`)
          .then((x) => x.data);
        if (!alive) return;
        setData(r);
      } catch (e) {
        toast.error(`Load language_pack failed: ${e.message}`);
      }
    })();
    return () => { alive = false; };
  }, [conceptId]);

  // Memoise the current slot's draft form so the editor never falls
  // out of sync when the activeLang or the data prop changes. This
  // replaces a previous `useEffect(setDraft, [activeLang, data])`
  // pattern that triggered the react-hooks/set-state-in-effect lint.
  const currentSlot = useMemo(() => {
    if (!data) return null;
    const slot = (data.language_pack || {})[activeLang] || {};
    return {
      candidate_task: slot.candidate_task || "",
      candidate_goal: slot.candidate_goal || "",
      scenario_text: typeof slot.scenario_text === "object"
        ? JSON.stringify(slot.scenario_text, null, 2)
        : (slot.scenario_text || ""),
      remediation_message: slot.remediation_message || "",
      reflection_prompt: slot.reflection_prompt || "",
      reflection_required_themes: Array.isArray(slot.reflection_required_themes)
        ? slot.reflection_required_themes.join(", ")
        : (slot.reflection_required_themes || ""),
    };
  }, [activeLang, data]);

  // The editable form starts from the memoised slot but can drift as
  // the admin types. `dirtyForLang` tracks which tab the draft
  // belongs to so we re-seed when the admin switches tab.
  const [draft, setDraft] = useState(null);
  const [draftLang, setDraftLang] = useState(null);
  if (currentSlot && draftLang !== activeLang) {
    setDraft(currentSlot);
    setDraftLang(activeLang);
  }

  const completeness = data?.completeness || {};

  const saveSlot = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      let scenario_text = null;
      if (draft.scenario_text && draft.scenario_text.trim()) {
        try { scenario_text = JSON.parse(draft.scenario_text); }
        catch (_e) {
          toast.error("scenario_text must be valid JSON. Save aborted.");
          setBusy(false); return;
        }
      }
      const themes = (draft.reflection_required_themes || "")
        .split(",").map((s) => s.trim()).filter(Boolean);
      const payload = {
        candidate_task: draft.candidate_task || null,
        candidate_goal: draft.candidate_goal || null,
        scenario_text,
        remediation_message: draft.remediation_message || null,
        reflection_prompt: draft.reflection_prompt || null,
        reflection_required_themes: themes,
      };
      const r = await api
        .put(`/admin/concept_configs/${conceptId}/language_pack/${activeLang}`, payload)
        .then((x) => x.data);
      setData((prev) => ({
        ...prev,
        language_pack: r.language_pack,
        completeness: r.completeness,
      }));
      toast.success(`Saved ${LANG_LABEL[activeLang]} slot.`);
    } catch (e) {
      toast.error(`Save failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  const runMt = async () => {
    if (activeLang === "en") {
      toast.error("Machine-translation runs from EN into AR or MI only.");
      return;
    }
    if (!window.confirm(
      `Replace the entire ${LANG_LABEL[activeLang]} slot with a machine-translated copy of the English slot?\n\n` +
      `Every translated string will be prefixed with "[MT — pending review] " so you can find them for human review.`,
    )) return;
    setMtBusy(true);
    try {
      const r = await api
        .post(`/admin/concept_configs/${conceptId}/language_pack/${activeLang}/mt`)
        .then((x) => x.data);
      setData((prev) => ({
        ...prev,
        language_pack: r.language_pack,
        completeness: r.completeness,
      }));
      toast.success(`MT seeded ${LANG_LABEL[activeLang]}. Review and edit before publishing.`);
    } catch (e) {
      toast.error(`MT failed: ${e.response?.data?.detail || e.message}`);
    } finally { setMtBusy(false); }
  };

  if (!data || !draft) {
    return (
      <div className="border border-[var(--iddl-rule)] bg-[var(--iddl-paper)] p-4 text-sm">
        Loading language pack for {conceptId}…
      </div>
    );
  }

  const SUPPORTED = ["en", "ar", "mi"];
  const fields = data.localised_fields || [
    "candidate_task", "candidate_goal", "scenario_text",
    "remediation_message", "reflection_prompt", "reflection_required_themes",
  ];

  return (
    <div
      className="border border-[var(--iddl-rule)] bg-white p-4 space-y-3"
      data-testid="iddl-language-pack-editor"
    >
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="label-mono">Language pack · {conceptId}</div>
        {onClose && (
          <Button onClick={onClose} variant="outline" className="rounded-none" data-testid="lpe-close">
            Close
          </Button>
        )}
      </div>

      {/* Tab strip */}
      <div className="flex items-center gap-1" data-testid="lpe-tabs">
        {SUPPORTED.map((lang) => {
          const slot = completeness[lang] || {};
          const filled = Object.values(slot).filter(Boolean).length;
          const total = fields.length;
          const isActive = activeLang === lang;
          return (
            <button
              key={lang}
              type="button"
              onClick={() => setActiveLang(lang)}
              className={`text-sm px-3 py-2 border ${isActive ? "border-[var(--iddl-ink)] bg-[var(--iddl-paper)]" : "border-[var(--iddl-rule)] bg-white"}`}
              data-testid={`lpe-tab-${lang}`}
            >
              {LANG_LABEL[lang]} ·{" "}
              <span className={filled === total ? "text-emerald-700" : filled === 0 ? "text-amber-700" : "text-zinc-600"}>
                {filled}/{total}
              </span>
            </button>
          );
        })}
        <div className="flex-1" />
        {activeLang !== "en" && (
          <Button
            onClick={runMt}
            disabled={mtBusy || busy}
            variant="outline"
            className="rounded-none"
            data-testid="lpe-mt-button"
            title="Auto-translate the English slot into this language using Gemini. Each translated string is prefixed with [MT — pending review] so you can find them later for human review."
          >
            {mtBusy ? "Translating…" : `MT seed → ${activeLang.toUpperCase()}`}
          </Button>
        )}
        <Button
          onClick={saveSlot}
          disabled={busy}
          className="rounded-none btn-ink"
          data-testid="lpe-save"
        >
          {busy ? "Saving…" : `Save ${activeLang.toUpperCase()}`}
        </Button>
      </div>

      {/* Field editors */}
      <div className="space-y-3" data-testid="lpe-fields">
        {fields.map((fld) => {
          const isJson = fld === "scenario_text";
          const isLong = ["candidate_task", "candidate_goal", "remediation_message", "reflection_prompt"].includes(fld);
          const rows = isJson ? 12 : (isLong ? 4 : 2);
          return (
            <div key={fld}>
              <div className="label-mono mb-1 flex items-center gap-2">
                <span>{fld}</span>
                {FIELD_HINT[fld] && (
                  <span className="text-[var(--iddl-muted)] text-xs font-normal">
                    {FIELD_HINT[fld]}
                  </span>
                )}
              </div>
              <Textarea
                value={draft[fld]}
                onChange={(e) => setDraft((p) => ({ ...p, [fld]: e.target.value }))}
                rows={rows}
                dir={activeLang === "ar" ? "rtl" : "ltr"}
                className="font-mono text-xs rounded-none"
                data-testid={`lpe-field-${fld}`}
                placeholder={activeLang !== "en" ? "(empty — runtime falls back to English)" : ""}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
